import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** 서버에서 불러오기. deps 가 바뀌거나 reload() 하면 다시 부른다. */
export function useApi<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    load().then(
      (d) => {
        if (alive) {
          setData(d);
          setError(undefined);
        }
      },
      (e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      alive = false;
    };
    // load 는 매 렌더 새로 만들어지므로 deps 로만 다시 부른다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    // 기기 모드: 동기화로 PC 쪽 변경이 들어오면 화면을 새로 불러온다
    window.addEventListener('apex:data-changed', reload);
    return () => window.removeEventListener('apex:data-changed', reload);
  }, [reload]);
  return { data, error, reload, setData };
}

/** 요소의 실제 폭(px) — SVG 차트를 글자 크기 그대로 그리기 위해. */
export function useWidth<T extends HTMLElement>(): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(Math.floor(el.clientWidth));
    const ro = new ResizeObserver((entries) => setWidth(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}
